import React, { useEffect, useMemo } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import useScrollIntoView from 'hooks/useScrollIntoView';

import { EMPTY_DOCUMENTS } from '../../MilestoneRoadmapWidget.constants';
import MilestoneRoadmapHero from '../MilestoneRoadmapHero';
import MilestoneRoadmapItem from '../MilestoneRoadmapItem';

import {
  ROADMAP_CURRENT_ROW_SCROLL_ID,
  ROADMAP_EYEBROW_CURRENT,
  ROADMAP_LIST_EYEBROW,
  ROADMAP_NO_DATA_TEXT,
  ROADMAP_NOT_STARTED_TEXT,
  ROADMAP_SKELETON_KEYS,
  ROADMAP_UP_NEXT_LABEL,
} from './MilestoneRoadmap.constants';
import {
  emptyStyles,
  footerLabelStyles,
  footerStyles,
  getFooterNextStyles,
  getFooterSwatchStyles,
  listEyebrowStyles,
  listStyles,
  scrollAreaStyles,
  skeletonStyles,
  wrapperStyles,
} from './MilestoneRoadmap.styles';
import {
  buildMilestoneRoadmapRows,
  getCurrentMilestone,
  getDaysInProcess,
  getInProgressLabel,
  getNextMilestone,
  getReachedCount,
  getRoadmapPercentage,
} from './MilestoneRoadmap.utils';

const MilestoneRoadmap = ({ progress = null, isLoading = false, documentsByDefinitionId = {} }) => {
  const { milestones, lastCompleted } = progress || {};

  const rows = useMemo(
    () => buildMilestoneRoadmapRows({ milestones, lastCompleted }),
    [milestones, lastCompleted],
  );
  const daysInProcess = useMemo(() => getDaysInProcess(milestones), [milestones]);
  const { scrollContainerRef, triggerScrollIntoView } = useScrollIntoView();
  const total = milestones?.length ?? 0;
  const reachedCount = getReachedCount(lastCompleted, total);
  const percentage = getRoadmapPercentage(reachedCount, total);

  const current = getCurrentMilestone(rows);
  const next = getNextMilestone(rows);
  const heroMilestone = current ?? rows[0] ?? null;
  const currentRowKey = current?.key ?? null;

  useEffect(() => {
    if (isLoading || !currentRowKey) {
      return;
    }

    triggerScrollIntoView({
      querySelector: `[data-scrollid="${ROADMAP_CURRENT_ROW_SCROLL_ID}"]`,
      block: 'center',
    });
  }, [currentRowKey, isLoading, triggerScrollIntoView]);

  if (isLoading) {
    return (
      <Box sx={wrapperStyles}>
        <Skeleton height={150} variant="rectangular" />
        {ROADMAP_SKELETON_KEYS.map(key => (
          <Skeleton height={34} key={key} sx={skeletonStyles} variant="rounded" />
        ))}
      </Box>
    );
  }

  if (!heroMilestone) {
    return (
      <Box sx={wrapperStyles}>
        <Typography sx={emptyStyles} variant="regular14">
          {ROADMAP_NO_DATA_TEXT}
        </Typography>
      </Box>
    );
  }

  const statusText = current ? getInProgressLabel(current.daysOnCurrent) : ROADMAP_NOT_STARTED_TEXT;

  return (
    <Box sx={wrapperStyles}>
      <MilestoneRoadmapHero
        currentName={heroMilestone.name}
        daysInProcess={daysInProcess}
        eyebrow={current ? ROADMAP_EYEBROW_CURRENT : ROADMAP_UP_NEXT_LABEL}
        percentage={percentage}
        reachedCount={reachedCount}
        statusText={statusText}
        stepNumber={reachedCount || 1}
        strongColor={heroMilestone.colors.strong}
        total={total}
      />

      <Box sx={listStyles}>
        <Typography sx={listEyebrowStyles} variant="regular12">
          {ROADMAP_LIST_EYEBROW}
        </Typography>

        <Box
          aria-label={ROADMAP_LIST_EYEBROW}
          ref={scrollContainerRef}
          role="region"
          sx={scrollAreaStyles}
          tabIndex={0}
        >
          {rows.map(row => (
            <MilestoneRoadmapItem
              colors={row.colors}
              date={row.date}
              daysOnCurrent={row.daysOnCurrent}
              documents={documentsByDefinitionId[row.definitionId] ?? EMPTY_DOCUMENTS}
              durationLabel={row.durationLabel}
              key={row.key}
              name={row.name}
              status={row.status}
            />
          ))}
        </Box>
      </Box>

      {current && next && (
        <Box sx={footerStyles}>
          <Box sx={getFooterSwatchStyles(next.colors.strong)} />
          <Typography sx={footerLabelStyles} variant="regular12">
            {ROADMAP_UP_NEXT_LABEL}
          </Typography>
          <Typography sx={getFooterNextStyles(next.colors)} variant="bold12">
            {next.name}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

MilestoneRoadmap.propTypes = {
  progress: PropTypes.shape({
    milestones: PropTypes.array,
    lastCompleted: PropTypes.object,
  }),
  isLoading: PropTypes.bool,
  documentsByDefinitionId: PropTypes.object,
};

export default MilestoneRoadmap;
